import { createAction } from '@reduxjs/toolkit';
import * as posDataConstants from './constants';
import type { AppData, Sale, SaleItem, Purchase, PurchaseItem, Credit, Customer, CustomerType } from '@/lib/types';

export const hydrateProducts = createAction(
  posDataConstants.HYDRATE_PRODUCTS,
  (products: AppData['products']) => ({ payload: { products } }),
);

export const hydrateServices = createAction(
  posDataConstants.HYDRATE_SERVICES,
  (services: AppData['services']) => ({ payload: { services } }),
);

export const hydratePrintMaterials = createAction(
  posDataConstants.HYDRATE_PRINT_MATERIALS,
  (materials: AppData['printMaterials']) => ({ payload: { materials } }),
);

export const hydrateCustomers = createAction(
  posDataConstants.HYDRATE_CUSTOMERS,
  (customers: AppData['customers']) => ({ payload: { customers } })
);

export const hydrateVendors = createAction(
  posDataConstants.HYDRATE_VENDORS,
  (vendors: AppData['vendors']) => ({ payload: { vendors } })
);

export const hydratePurchases = createAction(
  posDataConstants.HYDRATE_PURCHASES,
  (purchases: AppData['purchases']) => ({ payload: { purchases } })
);

export const hydratePaymentMethods = createAction(
  posDataConstants.HYDRATE_PAYMENT_METHODS,
  (paymentMethods: AppData['paymentMethods']) => ({ payload: { paymentMethods } })
);

export const hydrateSales = createAction(
  posDataConstants.HYDRATE_SALES,
  (sales: AppData['sales']) => ({ payload: { sales } })
);

export const hydrateCredits = createAction(
  posDataConstants.HYDRATE_CREDITS,
  (credits: AppData['credits']) => ({ payload: { credits } })
);

export const hydrateDueCollections = createAction(
  posDataConstants.HYDRATE_DUE_COLLECTIONS,
  (dueCollections: AppData['dueCollections']) => ({ payload: { dueCollections } })
);

export const hydrateExpenses = createAction(
  posDataConstants.HYDRATE_EXPENSES,
  (expenses: AppData['expenses']) => ({ payload: { expenses } })
);

export const saveProduct = createAction(
  posDataConstants.SAVE_PRODUCT,
  (product: AppData['products'][0], isNew: boolean) => ({ payload: { product, isNew } }),
);

export const saveService = createAction(
  posDataConstants.SAVE_SERVICE,
  (service: AppData['services'][0], isNew: boolean) => ({ payload: { service, isNew } }),
);

export const savePrintMaterial = createAction(
  posDataConstants.SAVE_PRINT_MATERIAL,
  (material: AppData['printMaterials'][0], isNew: boolean) => ({ payload: { material, isNew } }),
);

export const removePrintMaterial = createAction(
  posDataConstants.REMOVE_PRINT_MATERIAL,
  (id: string) => ({ payload: { id } }),
);

export const saveCustomer = createAction(
  posDataConstants.SAVE_CUSTOMER,
  (customer: AppData['customers'][0], isNew: boolean) => ({ payload: { customer, isNew } }),
);

export const saveVendor = createAction(
  posDataConstants.SAVE_VENDOR,
  (vendor: AppData['vendors'][0], isNew: boolean) => ({ payload: { vendor, isNew } }),
);

export const saveStock = createAction(
  posDataConstants.SAVE_STOCK,
  (productId: string, mode: 'add' | 'adjust', quantity: number, reason: string) => ({
    payload: { productId, mode, quantity, reason },
  }),
);

export const cancelSale = createAction(
  posDataConstants.CANCEL_SALE,
  (sale: Sale) => ({ payload: { sale } }),
);

export const settleCredit = createAction(
  posDataConstants.SETTLE_CREDIT,
  (credit: Credit) => ({ payload: { credit } }),
);

export const addPaymentMethod = createAction(
  posDataConstants.ADD_PAYMENT_METHOD,
  (name: string) => ({ payload: { name } }),
);

export const removePaymentMethod = createAction(
  posDataConstants.REMOVE_PAYMENT_METHOD,
  (id: string) => ({ payload: { id } }),
);

export const completeSale = createAction(
  posDataConstants.COMPLETE_SALE,
  (cart: SaleItem[], paymentMethod: string, total: number, customerId: string | undefined, payNow: number, customerName?: string, customerType?: CustomerType) => ({
    payload: { cart, paymentMethod, total, customerId, payNow, customerName, customerType },
  }),
);

export const recordPurchase = createAction(
  posDataConstants.RECORD_PURCHASE,
  (purchase: { vendorId?: string; items: PurchaseItem[]; paidAmount: number }) => ({
    payload: purchase,
  }),
);

export const payPurchase = createAction(
  posDataConstants.PAY_PURCHASE,
  (purchaseId: string, amount: number) => ({ payload: { purchaseId, amount } }),
);

export const cancelPurchase = createAction(
  posDataConstants.CANCEL_PURCHASE,
  (purchase: Purchase) => ({ payload: { purchase } }),
);

export const setDateFormat = createAction(
  posDataConstants.SET_DATE_FORMAT,
  (format: 'BS' | 'AD') => ({ payload: { format } }),
);

export const initializeSeed = createAction(
  posDataConstants.INITIALIZE_SEED,
  (data: AppData) => ({ payload: { data } }),
);
